import { useCallback, useState } from 'react';
import {
  buildAuthorityRecord,
  formatAuthorityForLinking,
} from '../utils/authorityCreation';
import { useAuthorityApi } from '../utils/authorityApi';
import {
  createAuthorityRoute,
  getAuthorityTypeForField,
} from '../../../constants';
import {
  getAuthoritySourceForField,
  getAuthoritySettings
} from '../../../utils/configUtils';

export const useAuthorityCreation = (tenantId = null, authoritySettings = {}) => {
  const [isCreating, setIsCreating] = useState(false);
  const { createAuthorityRecord } = useAuthorityApi(tenantId);

  const createInstantAuthority = useCallback(
    async (
      textValue,
      fieldPath,
      formValues = {},
      arrayIndex = 0,
      controlledVocab = {},
      authoritySource = null,
    ) => {
      if (!textValue || textValue.length < 3) {
        throw new Error('Text value must be at least 3 characters');
      }

      setIsCreating(true);

      try {
        // Get authority source from controlledVocab or parameter
        const sourceToUse = authoritySource || getAuthoritySourceForField(fieldPath, controlledVocab);

        const recordData = buildAuthorityRecord(
          textValue,
          fieldPath,
          formValues,
          arrayIndex,
          controlledVocab,
          sourceToUse,
        );


        const response = await createAuthorityRecord(recordData);


        if (!response) {
          throw new Error('No response received from authority creation API');
        }

        const formattedAuthority = formatAuthorityForLinking(
          response,
          textValue,
          fieldPath,
          formValues,
          arrayIndex,
          controlledVocab,
        );


        return formattedAuthority;
      } catch (error) {
        console.error('Failed to create authority record:', error);
        throw error;
      } finally {
        setIsCreating(false);
      }
    },
    [createAuthorityRecord],
  );

  const createAdvancedAuthority = useCallback(
    (textValue, fieldPath, formValues, arrayIndex, controlledVocab, authoritySource = null) => {
      const authorityConfig = getAuthorityTypeForField(
        fieldPath,
        formValues,
        arrayIndex,
        controlledVocab,
      );

      // Get authority source from controlledVocab or parameter
      const sourceToUse = authoritySource || getAuthoritySourceForField(fieldPath, controlledVocab);

      const params = new URLSearchParams({
        prefill: textValue,
        authorityType: authorityConfig?.searchType || 'personalName',
        source: sourceToUse || 'local',
        fieldType: fieldPath,
        sourceApp: 'ui-plugin-inventory-templates',
      });

      const url = `${createAuthorityRoute}?${params.toString()}`;
      window.open(url, '_blank', 'noopener,noreferrer');


    },
    [],
  );

  const handleCreateAuthority = useCallback(
    (type, textValue, fieldPath, formValues, arrayIndex, controlledVocab) => {
      if (type === 'instant') {
        return createInstantAuthority(
          textValue,
          fieldPath,
          formValues,
          arrayIndex,
          controlledVocab,
        );
      } else if (type === 'advanced') {
        return createAdvancedAuthority(
          textValue,
          fieldPath,
          formValues,
          arrayIndex,
          controlledVocab,
        );
      } else {
        throw new Error(`Unknown creation type: ${type}`);
      }
    },
    [createInstantAuthority, createAdvancedAuthority],
  );

  return {
    createInstantAuthority,
    createAdvancedAuthority,
    handleCreateAuthority,
    isCreating,
  };
};
